import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  CancelPropertyTaxItem,
  CreatePropertyTaxItem,
  PayPropertyTaxItem,
  PropertyTaxItem,
  PropertyTaxStatus,
  PropertyTaxSummary,
  PropertyTaxType,
} from '@dcc-erp/shared';

import { api } from '../api';

// Cache-key scope — keyed by companyId so DCC/TTOP/RCI switches give
// independent caches (same pattern as ar.ts / lva.ts / prepaid.ts).
const propTaxKey = (companyId: string | undefined) => ['property-tax', companyId] as const;

export interface PropertyTaxFilters {
  year?: number;
  taxType?: PropertyTaxType;
  branchId?: string;
  departmentId?: string;
  status?: PropertyTaxStatus | 'OVERDUE';
}

export function usePropertyTaxItems(
  companyId: string | undefined,
  filters: PropertyTaxFilters = {},
) {
  const qs = new URLSearchParams();
  if (filters.year !== undefined) qs.set('year', String(filters.year));
  if (filters.taxType) qs.set('taxType', filters.taxType);
  if (filters.branchId) qs.set('branchId', filters.branchId);
  if (filters.departmentId) qs.set('departmentId', filters.departmentId);
  if (filters.status) qs.set('status', filters.status);
  const search = qs.toString();
  return useQuery({
    queryKey: [...propTaxKey(companyId), filters],
    queryFn: () =>
      api<PropertyTaxItem[]>(`/companies/${companyId}/property-tax${search ? `?${search}` : ''}`),
    enabled: !!companyId,
  });
}

export function usePropertyTaxItem(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['property-tax', companyId, id],
    queryFn: () => api<PropertyTaxItem>(`/companies/${companyId}/property-tax/${id}`),
    enabled: !!companyId && !!id,
  });
}

export function usePropertyTaxSummary(companyId: string | undefined, year: number | undefined) {
  return useQuery({
    queryKey: [...propTaxKey(companyId), 'summary', year],
    queryFn: () =>
      api<PropertyTaxSummary>(`/companies/${companyId}/property-tax/summary?year=${year}`),
    enabled: !!companyId && year !== undefined,
  });
}

export function useCreatePropertyTaxItem(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      data,
      idempotencyKey,
    }: {
      data: CreatePropertyTaxItem;
      idempotencyKey?: string;
    }) =>
      api<PropertyTaxItem>(`/companies/${companyId}/property-tax`, {
        method: 'POST',
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: propTaxKey(companyId) });
      // Create posts an accrual JV — invalidate journal-entries so the GL
      // view reflects it without a manual refetch.
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
      void qc.invalidateQueries({ queryKey: ['dashboard', companyId] });
    },
  });
}

export function usePayPropertyTaxItem(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
      idempotencyKey,
    }: {
      id: string;
      data: PayPropertyTaxItem;
      idempotencyKey?: string;
    }) =>
      api<PropertyTaxItem>(`/companies/${companyId}/property-tax/${id}/pay`, {
        method: 'POST',
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: propTaxKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
    },
  });
}

export function useCancelPropertyTaxItem(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CancelPropertyTaxItem }) =>
      api<PropertyTaxItem>(`/companies/${companyId}/property-tax/${id}/cancel`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: propTaxKey(companyId) });
    },
  });
}
